import { Button } from "@progress/kendo-react-buttons";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router";
import * as s from "./RouteErrorBoundary.css";

// ---------------------------------------------------------------------------
// <RouteErrorBoundary> — shared error fallback wired in as
// `errorElement` on every data-loading route.
//
// Cross-page fix from audit/21-visual-diff.md §11/§12: pages like
// EditReward and DashboardCustomization could get wedged on "Loading…"
// when the underlying query threw, because the page-local error branches
// were either missing or only fired for axios errors (not router
// throwUnless-style 404s). React Router's errorElement catches BOTH —
// query-thrown errors and Response-throwing loaders — at the route
// boundary, giving the user a predictable "something went wrong" UI with
// real recovery affordances.
//
// Surface:
//   - Plain-english headline ("We hit a problem loading this page")
//   - The actual error message (data.message, error.statusText, or
//     error.message — whatever the runtime hands us)
//   - "Go back" → navigate(-1) — falls back to the dashboard when there's
//     no history entry to pop (cold-loaded a bad URL)
//   - "Retry" → router 'replace' to the current URL, which forces React
//     Router to re-run the loader / re-mount the page so the failed
//     query refires
// ---------------------------------------------------------------------------

interface Props {
  /** Title override — useful for embedded boundaries inside a sub-route. */
  title?: string;
}

const formatError = (error: unknown): { headline: string; detail: string } => {
  // React Router's `throw new Response(...)` shape (loaders, action errors).
  if (isRouteErrorResponse(error)) {
    const status = error.status;
    const statusText = error.statusText;
    const dataMessage =
      typeof error.data === "string"
        ? error.data
        : typeof error.data === "object" && error.data
          ? (error.data as { message?: string }).message
          : undefined;
    // For 404 give a more pointed headline; everything else gets the
    // generic "We hit a problem" message + the status code as detail.
    const headline =
      status === 404
        ? "We couldn't find that page"
        : "We hit a problem loading this page";
    const detail = dataMessage ?? statusText ?? `HTTP ${status}`;
    return { headline, detail };
  }
  // Plain `Error` instance — anything react-query throws, anything a
  // child component throws during render.
  if (error instanceof Error) {
    return {
      headline: "We hit a problem loading this page",
      detail: error.message || error.name,
    };
  }
  // Bare-string or unknown — best-effort stringify.
  return {
    headline: "We hit a problem loading this page",
    detail: typeof error === "string" ? error : "Unknown error.",
  };
};

const RouteErrorBoundary = ({ title }: Props) => {
  const error = useRouteError();
  const navigate = useNavigate();
  const { headline, detail } = formatError(error);

  const handleBack = () => {
    // navigate(-1) is a no-op when there's no history entry; fall back to
    // the dashboard so the user is never stranded.
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const handleRetry = () => {
    // 'replace' + same URL forces React Router to re-mount the route, which
    // re-runs any queries inside the component tree.
    navigate(window.location.pathname + window.location.search, {
      replace: true,
    });
  };

  return (
    <div className={s.wrap} role="alert" aria-live="assertive">
      <div className={s.card}>
        <h2 className={s.headline}>{title ?? headline}</h2>
        <p className={s.detail}>{detail}</p>
        <div className={s.actions}>
          <Button type="button" onClick={handleBack}>
            Go back
          </Button>
          <Button type="button" themeColor="primary" onClick={handleRetry}>
            Retry
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RouteErrorBoundary;
